import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VisitorAnalyticsPanel = ({ visitors, onExportData }) => {
  const [sortBy, setSortBy] = useState('lastVisit');
  const [sortOrder, setSortOrder] = useState('desc');

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  const sortedVisitors = [...visitors]?.sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a?.name?.localeCompare(b?.name);
        break;
      case 'lastVisit':
        comparison = new Date(b.lastVisit) - new Date(a.lastVisit);
        break;
      case 'downloads':
        comparison = b?.totalDownloads - a?.totalDownloads;
        break;
      case 'timeSpent':
        comparison = b?.timeSpent - a?.timeSpent;
        break;
      default:
        comparison = 0;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-6">
        <div>
          <h3 className="text-base md:text-lg font-semibold mb-1">
            Visitor Analytics
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Track engagement and downloads
          </p>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          iconName="Download"
          iconPosition="left"
          onClick={onExportData}
        >
          Export Data
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="p-3 md:p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Users" size={16} className="text-primary" />
            <span className="text-xs text-muted-foreground">Total Visitors</span>
          </div>
          <p className="text-xl md:text-2xl font-semibold data-text">{visitors?.length}</p>
        </div>

        <div className="p-3 md:p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Download" size={16} className="text-success" />
            <span className="text-xs text-muted-foreground">Total Downloads</span>
          </div>
          <p className="text-xl md:text-2xl font-semibold data-text">
            {visitors?.reduce((sum, v) => sum + v?.totalDownloads, 0)}
          </p>
        </div>

        <div className="p-3 md:p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Clock" size={16} className="text-accent" />
            <span className="text-xs text-muted-foreground">Avg. Time</span>
          </div>
          <p className="text-xl md:text-2xl font-semibold data-text">
            {formatDuration(Math.floor(visitors?.reduce((sum, v) => sum + v?.timeSpent, 0) / visitors?.length))}
          </p>
        </div>

        <div className="p-3 md:p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Eye" size={16} className="text-warning" />
            <span className="text-xs text-muted-foreground">Total Views</span>
          </div>
          <p className="text-xl md:text-2xl font-semibold data-text">
            {visitors?.reduce((sum, v) => sum + v?.viewCount, 0)}
          </p>
        </div>
      </div>
      <div className="overflow-x-auto -mx-4 md:mx-0">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full">
            <thead className="bg-muted">
              <tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium cursor-pointer hover:bg-muted/80"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-2">
                    <span>Visitor</span>
                    {sortBy === 'name' && (
                      <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium hidden md:table-cell">
                  Contact
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-medium cursor-pointer hover:bg-muted/80"
                  onClick={() => handleSort('downloads')}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>Downloads</span>
                    {sortBy === 'downloads' && (
                      <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-center text-xs font-medium cursor-pointer hover:bg-muted/80 hidden lg:table-cell"
                  onClick={() => handleSort('timeSpent')}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>Time Spent</span>
                    {sortBy === 'timeSpent' && (
                      <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-right text-xs font-medium cursor-pointer hover:bg-muted/80"
                  onClick={() => handleSort('lastVisit')}
                >
                  <div className="flex items-center justify-end gap-2">
                    <span>Last Visit</span>
                    {sortBy === 'lastVisit' && (
                      <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sortedVisitors?.map((visitor) => (
                <tr key={visitor?.id} className="hover:bg-muted/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm md:text-base font-semibold text-primary">
                          {visitor?.name?.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{visitor?.name}</p>
                        <p className="text-xs text-muted-foreground md:hidden">{visitor?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="text-xs space-y-1">
                      <p className="text-muted-foreground">{visitor?.email}</p>
                      <p className="text-muted-foreground data-text">{visitor?.phone}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded text-xs font-medium data-text">
                      <Icon name="Download" size={12} />
                      {visitor?.totalDownloads}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center hidden lg:table-cell">
                    <span className="text-sm data-text">{formatDuration(visitor?.timeSpent)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <p className="text-xs md:text-sm whitespace-nowrap">{formatDate(visitor?.lastVisit)}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VisitorAnalyticsPanel;